// Componente CartItem - Item individual del carrito
// Muestra la información de un producto en el carrito con opciones para modificar cantidad y eliminar

import { useState } from 'react'
import { useCart } from '../hooks/useCart'
import { useBusinessRulesContext } from '../hooks/useBusinessRulesContext'
import { formatCurrency } from '../utils/helpers'
import { getImageUrl } from '../services/cloudinary.service'
import { Link } from 'react-router-dom'
import ConfirmDialog from './ConfirmDialog'
import './CartItem.css'

/**
 * Componente CartItem
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.item - Item del carrito
 */
function CartItem({ item }) {
  const { removeFromCart, updateQuantity } = useCart()
  const { rules } = useBusinessRulesContext()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }
  const allowPurchaseWithoutStock = outOfStockPolicy.purchaseBehavior === 'allow_purchase'
  const maxQuantity = allowPurchaseWithoutStock ? 9999 : (item.stock || 999)
  const hasStockInfo = item.stock !== null && item.stock !== undefined
  const isOutOfStock = hasStockInfo ? (item.stock || 0) <= 0 : false
  const exceedsStock = hasStockInfo ? (item.quantity || 0) > (item.stock || 0) : false

  // Obtener URL de la imagen
  // Manejar diferentes formatos: puede ser fileId, url completa, o null
  let imageUrl = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"%3E%3Crect fill="%23f5f0eb" width="400" height="400"/%3E%3C/svg%3E'
  
  if (item.image) {
    try {
      // Si es una URL completa, usarla directamente con transformaciones
      if (item.image.startsWith('http')) {
        imageUrl = getImageUrl(item.image, { width: 200, height: 200, crop: 'fill' })
      } else {
        // Si es un fileId, asegurarse de que incluya la carpeta si no la tiene
        // Cloudinary public_id puede ser "products/xxx" o solo "xxx"
        let fileId = item.image
        // Si no empieza con "products/", agregarlo (asumiendo que las imágenes están en products/)
        if (!fileId.includes('/') && !fileId.startsWith('http')) {
          fileId = `products/${fileId}`
        }
        imageUrl = getImageUrl(fileId, { width: 200, height: 200, crop: 'fill' })
      }
    } catch (error) {
      console.error('Error obteniendo URL de imagen del carrito:', error, 'item.image:', item.image)
    }
  } else {
    // Debug: verificar si el item tiene imagen
    console.warn('Item del carrito sin imagen:', { productId: item.productId, productName: item.productName, image: item.image })
  }

  // Calcular subtotal del item
  const itemSubtotal = item.price * item.quantity

  // Manejar cambio de cantidad
  const handleQuantityChange = (e) => {
    const newQuantity = parseInt(e.target.value) || 1
    updateQuantity(item.id, newQuantity)
  }

  // Manejar incremento de cantidad
  const handleIncrease = () => {
    const newQuantity = item.quantity + 1
    if (allowPurchaseWithoutStock || newQuantity <= (item.stock || 999)) {
      updateQuantity(item.id, newQuantity)
    }
  }

  // Manejar decremento de cantidad
  const handleDecrease = () => {
    if (item.quantity > 1) {
      updateQuantity(item.id, item.quantity - 1)
    }
  }

  // Manejar eliminación
  const handleRemove = () => {
    setShowConfirmDialog(true)
  }

  const handleConfirmRemove = () => {
    removeFromCart(item.id)
  }

  return (
    <>
      <div className="cart-item">
        {/* Imagen del producto */}
        <Link to={`/productos/${item.productId}`} className="cart-item-image">
          <img src={imageUrl} alt={item.productName} />
        </Link>

      {/* Información del producto */}
      <div className="cart-item-info">
        <Link to={`/productos/${item.productId}`} className="cart-item-name">
          {item.productName}
        </Link>
        <p className="cart-item-sku">SKU: {item.sku}</p>
        {item.variant && (
          <p className="cart-item-variant">
            {item.variant.type}: <strong>{item.variant.name}</strong>
          </p>
        )}
        <p className="cart-item-price">
          {formatCurrency(item.price)} c/u
        </p>
      </div>

      {/* Selector de cantidad */}
      <div className="cart-item-quantity">
        <label htmlFor={`quantity-${item.id}`} className="quantity-label">
          Cantidad
        </label>
        <div className="quantity-selector">
          <button
            type="button"
            className="quantity-btn quantity-btn-decrease"
            onClick={handleDecrease}
            aria-label="Disminuir cantidad"
            disabled={item.quantity <= 1}
          >
            −
          </button>
          <input
            type="number"
            id={`quantity-${item.id}`}
            className="quantity-input"
            value={item.quantity}
            onChange={handleQuantityChange}
            min="1"
            max={maxQuantity}
            aria-label="Cantidad"
          />
          <button
            type="button"
            className="quantity-btn quantity-btn-increase"
            onClick={handleIncrease}
            aria-label="Aumentar cantidad"
            disabled={!allowPurchaseWithoutStock && item.quantity >= (item.stock || 999)}
          >
            +
          </button>
        </div>
        {isOutOfStock && !allowPurchaseWithoutStock ? (
          <p className="stock-info">
            {outOfStockPolicy.labelText || 'Sin stock'}
          </p>
        ) : null}
        {exceedsStock && outOfStockPolicy.purchaseBehavior !== 'allow_purchase' ? (
          <p className="stock-info">
            Cantidad supera el stock disponible ({item.stock})
          </p>
        ) : null}
        {item.stock && item.stock > 0 && (
          <p className="stock-info">
            Stock disponible: {item.stock}
          </p>
        )}
      </div>

      {/* Subtotal */}
      <div className="cart-item-subtotal">
        <p className="subtotal-label">Subtotal</p>
        <p className="subtotal-amount">{formatCurrency(itemSubtotal)}</p>
      </div>

      {/* Botón eliminar */}
      <button
        className="cart-item-remove"
        onClick={handleRemove}
        aria-label="Eliminar producto"
        title="Eliminar del carrito"
      >
        ✕
      </button>
    </div>
    
    {/* Diálogo de confirmación */}
    <ConfirmDialog
      isOpen={showConfirmDialog}
      onClose={() => setShowConfirmDialog(false)}
      onConfirm={handleConfirmRemove}
      title="Eliminar producto"
      message={`¿Estás seguro de que deseas eliminar "${item.productName}" del carrito?`}
      confirmText="Eliminar"
      cancelText="Cancelar"
      variant="danger"
    />
    </>
  )
}

export default CartItem

